import React, { useState } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./Login.css";
import { XCircleIcon, CheckCircleIcon } from "@heroicons/react/20/solid";

const Login = ({ setUser }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrors([]);
    setSuccessMessage("");
    const email = username.toLowerCase();
    const newErrors = [];

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          newErrors.push("Passwords do not match");
        }
        if (password.length < 8) {
          newErrors.push("Password must be at least 8 characters long");
        }
        if (!/\d/.test(password)) {
          newErrors.push("Password must contain at least one number");
        }
        if (!/[A-Z]/.test(password)) {
          newErrors.push("Password must contain at least one uppercase letter");
        }
        if (!/[a-z]/.test(password)) {
          newErrors.push("Password must contain at least one lowercase letter");
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
          newErrors.push(
            "Password must contain at least one special character",
          );
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors.push("Please enter a valid email address");
        }
        if (username.length < 3) {
          newErrors.push("Username must be at least 3 characters long");
        }
        if (newErrors.length === 0) {
          await Auth.signUp({
            username: email,
            password,
            attributes: { email },
          });
          setSuccessMessage(
            "Sign up successful! Please check your email to confirm your account.",
          );
        } else {
          // If there are validation errors, throw them to be caught below
          throw new Error("Validation failed");
        }
      } else {
        const user = await Auth.signIn(email, password);
        console.log("Login successful");
        setUser(user);
        setSuccessMessage("Log In successful! Redirecting...");
        navigate("/");
      }
    } catch (error) {
      console.error("Authentication error:", error);

      if (error.message === "Validation failed") {
        // Validation errors are already in newErrors
      } else {
        switch (error.code) {
          case "UserNotConfirmedException":
            newErrors.push(
              "Your account is not confirmed. Please check your email for the confirmation link.",
            );
            break;
          case "UsernameExistsException":
            newErrors.push(
              "An account with this email already exists. Please try logging in or use a different email.",
            );
            break;
          case "LimitExceededException":
            newErrors.push(
              "Too many attempts. Please try again later or contact support.",
            );
            break;
          case "UserNotFoundException":
            newErrors.push(
              "No account found with this email. Please check your email or sign up.",
            );
            break;
          case "NotAuthorizedException":
            newErrors.push("Incorrect username or password. Please try again.");
            break;
          default:
            if (error.message.includes("USER_ID_FOR_SRP")) {
              newErrors.push(
                "There was an issue with the authentication process. Please try again or contact support.",
              );
            } else {
              newErrors.push(
                error.message ||
                  "An unexpected error occurred. Please try again.",
              );
            }
        }
      }
    } finally {
      setErrors(newErrors);
      if (newErrors.length > 0) {
        newErrors.forEach((err) =>
          toast.error("Authentication failed: " + err),
        );
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>
          {isSignUp
            ? "Hey stranger, welcome to your MO@T App. Enter your Thiess email below to get into it..."
            : "Hey... Welcome back to your MO@T App, we missed you!"}
        </h2>
        <div className="tab-container">
          <button
            className={`tab ${!isSignUp ? "active" : ""}`}
            onClick={() => setIsSignUp(false)}
          >
            Sign in
          </button>
          <button
            className={`tab ${isSignUp ? "active" : ""}`}
            onClick={() => setIsSignUp(true)}
          >
            Sign up
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label>Email</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your Email"
            required
          />
          <label>Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your Password"
            required
          />
          {isSignUp && (
            <>
              <label>Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your Password"
                required
              />
            </>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="sm:text-lg text-sm"
          >
            {isLoading ? (
              <svg
                aria-hidden="true"
                className="w-5 h-5 text-gray-200 animate-spin fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            ) : (
              "Let's get cracking"
            )}
          </button>
        </form>
        {successMessage && !errors.length && (
          <div className="mt-4 rounded-md w-full bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon
                  className="h-5 w-5 text-green-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <p className="sm:text-lg text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        )}
        {errors.length > 0 && (
          <div className="mt-4 rounded-md w-full bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <p className="sm:text-lg text-sm font-medium text-red-800">
                  There {errors.length === 1 ? "was an error" : "were errors"}{" "}
                  with your submission:
                </p>
                <ul className="mt-2 sm:text-sm text-xs text-red-700 list-disc list-inside">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="forgot-password">
          <a href="/forgot-password">Forgot Password?</a>
        </div>
        <div className="terms">
          <p>
            By signing up, you agree to our <a href="/terms">Terms</a>,{" "}
            <a href="/privacy">Data Policy</a> and{" "}
            <a href="/cookies">Cookies Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
